import { PropTypes, React } from 'imports';
import DropPanel, { CloseMode } from '../DropPanel/DropPanel';
import { FormattedResource } from '..';
import { SketchPicker } from 'react-color';
import './ColorPicker.less';

// TODO support other color formats, make tests more precise (i. e. rgb(999,999,999) will match)
const hexRE = /^#[\da-f]{3,6}$/i,
    hexaRE = /^#[\da-f]{6,8}$/i,
    rgbRE = /^rgb\(\d{1,3},\s*\d{1,3},\s*\d{1,3}\)$/i,
    rgbaRE = /^rgba\(\d{1,3},\s*\d{1,3},\s*\d{1,3},\s*\d{1,3}\)$/i,
    res = [hexRE, hexaRE, rgbRE, rgbaRE],

    isPlainColor = value => typeof value === 'string' && res.some(re => re.test(value)),
    isTitledColor = value => typeof value === 'object' && value && (!value.color || isPlainColor(value.color)) && FormattedResource.isResource(value.title),
    isColor = value => isPlainColor(value) || isTitledColor(value);

export const plainColorPropType = (props, propName, componentName) => {
    if (props[propName] && !isPlainColor(props[propName])) {
        return new Error(`Invalid prop \`${propName}\` supplied to \`${componentName}\`. \`${props[propName]}\` is not a color.`);
    }
};

plainColorPropType.isRequired = (props, propName, componentName) => {
    if (!props[propName]) {
        return new Error(`Invalid prop \`${propName}\` supplied to \`${componentName}\`. It is required, but \`${props[propName]}\`.`);
    }
    return plainColorPropType(props, propName, componentName);
};

export const titledColorPropType = PropTypes.shape({
    color: plainColorPropType.isRequired,
    title: FormattedResource.resourcePropType.isRequired
});

export const colorPropType = PropTypes.oneOfType([plainColorPropType, titledColorPropType]);

export const DEFAULT_PALETTE = ['#D0021B', '#F5A623', '#F8E71C', '#8B572A', '#7ED321', '#417505', '#BD10E0', '#9013FE', '#4A90E2', '#50E3C2', '#B8E986', '#000000', '#4A4A4A', '#9B9B9B', '#FFFFFF'];

export default class ColorPicker extends React.PureComponent {
    static get isPlainColor() {
        return isPlainColor;
    }

    static get isTitledColor() {
        return isTitledColor;
    }

    static get isColor() {
        return isColor;
    }

    static get plainColorPropType() {
        return plainColorPropType;
    }

    static get titledColorPropType() {
        return titledColorPropType();
    }

    static get colorPropType() {
        return colorPropType;
    }

    static get DEFAULT_PALETTE() {
        return DEFAULT_PALETTE;
    }

    static get propTypes() {
        return {
            palette: PropTypes.arrayOf(colorPropType),
            value: plainColorPropType,
            placeholder: PropTypes.oneOfType([colorPropType, FormattedResource.resourcePropType]),
            clearable: PropTypes.bool,
            disabled: PropTypes.bool,
            tabIndex: PropTypes.number,
            className: PropTypes.string,
            onChange: PropTypes.func,
            onClose: PropTypes.func
        };
    }

    static get defaultProps() {
        return {
            value: null,
            clearable: true,
            disabled: false,
            className: ''
        };
    }

    _onChange = (value) => {
        if (this.props.onChange) {
            this.props.onChange(this, value.hex);
        }
    };

    _onClose = (value) => {
        if (this.props.onClose) {
            this.props.onClose(this, isPlainColor(value) ? value : value?.hex || '');
        }
    };

    _onClearClick = (event) => {
        this._onChange(this, '');
        event.stopPropagation();
    };

    render() {
        const { value, placeholder, palette, clearable, disabled, className } = this.props,


            placeholderColor = isPlainColor(placeholder) ? placeholder : isTitledColor(placeholder) ? placeholder.color : '#FFFFFF',
            placeholderContent = isTitledColor(placeholder) ? <FormattedResource resource={placeholder.title} /> : null;


        return (
            <DropPanel
                className={`color-picker ${className}`}
                disabled={disabled}
                title={
                    <div className={`swatch ${value ? 'content' : 'placeholder'}`} style={{ backgroundColor: value || placeholderColor }} >
                        {!value ? placeholderContent : null}
                    </div>
                }
                // eslint-disable-next-line react/jsx-handler-names
                icon={value && clearable ? <span className="fa fa-times" onClick={!disabled ? this._onClearClick : null} /> : DropPanel.AUTO_ICON}
                tabIndex={this.props.tabIndex}
                closeMode={[CloseMode.BUTTON, CloseMode.OUTSIDE]}
                onClose={this._onClose.bind(this, value)}
            >
                <SketchPicker
                    disableAlpha
                    presetColors={palette}
                    color={value || placeholderColor}
                    onChangeComplete={this._onChange} />
                {
                    clearable && placeholder ?
                        <div className="no-color" >
                            <div className="swatch" style={{ backgroundColor: placeholderColor }} onClick={this._onClearClick}>
                                {placeholderContent}
                            </div>
                        </div> :
                        null
                }
            </DropPanel >
        );
    }
}
